// @repo/observability/errors — Error reporting interfaces and utilities

/**
 * Sink for captured errors. The `extra` bag is the flattened logger context +
 * metadata (see `createServerLogger` / `createClientLogger`), so two keys are
 * **reserved** and interpreted rather than merely attached by the Sentry
 * reporter: `logLevel` (event severity) and `sessionId` (event identity —
 * Sentry `user.id`, which is what affected-user counts key off). Both are
 * stamped by the loggers themselves and must not be passed as metadata.
 */
export interface ErrorReporter {
  captureException(error: Error, extra?: Record<string, unknown>): void
  captureMessage(message: string, extra?: Record<string, unknown>): void
}

/**
 * Marks an error as already captured by an in-process reporting boundary (e.g. an oRPC error
 * middleware), so a framework-level last-resort handler such as Next's `onRequestError` can skip
 * it instead of reporting the same instance twice. A non-enumerable symbol so it cannot collide
 * with a real field and never widens the serialized error shape. Tagging is best-effort: callers
 * sit in `catch` blocks about to rethrow, so a non-extensible throwable is left untagged rather
 * than letting a `TypeError` replace the original error. The marker is also carried in `digest` so
 * React/Next error processing can preserve it when the original error instance is replaced.
 */
const ERROR_REPORTED: unique symbol = Symbol.for('@repo/observability/error-reported')
/** `digest` survives React's Server Component error processing when the instance does not. */
const ERROR_REPORTED_DIGEST_PREFIX = 'error-reported:'

export function markErrorReported(error: unknown): void {
  if (typeof error !== 'object' || error === null || !Object.isExtensible(error)) return
  Object.defineProperty(error, ERROR_REPORTED, { value: true, enumerable: false })
  if (!('digest' in error) || error.digest === undefined) {
    const name = error instanceof Error ? error.name : 'unknown'
    Object.defineProperty(error, 'digest', {
      value: `${ERROR_REPORTED_DIGEST_PREFIX}${name}`,
      enumerable: false,
      configurable: true,
      writable: true
    })
  }
}

export function isErrorReported(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false
  if (ERROR_REPORTED in error && error[ERROR_REPORTED] === true) return true
  return (
    'digest' in error &&
    typeof error.digest === 'string' &&
    error.digest.startsWith(ERROR_REPORTED_DIGEST_PREFIX)
  )
}

/**
 * Create a composite error reporter that fans out to all provided sinks.
 */
export function createErrorReporter(...sinks: ErrorReporter[]): ErrorReporter {
  return {
    captureException(error: Error, extra?: Record<string, unknown>) {
      for (const sink of sinks) {
        try {
          sink.captureException(error, extra)
        } catch {
          /* best-effort: don't let one sink abort the fan-out */
        }
      }
    },
    captureMessage(message: string, extra?: Record<string, unknown>) {
      for (const sink of sinks) {
        try {
          sink.captureMessage(message, extra)
        } catch {
          /* best-effort: don't let one sink abort the fan-out */
        }
      }
    }
  }
}
